import { useEffect, useMemo, useRef, useState } from 'react';
import { Source, Layer } from 'react-map-gl';
import { useAnimate } from 'framer-motion';
import { point } from '@turf/helpers';
import { COLOR_NEUTRAL_WHITE } from '@alltrails/denali/tokens';
import { MARKER_PULSE_SOURCE_ID, MARKER_PULSE_LAYER_ID } from './Markers';

interface PulsingMarkerProps {
  activeSegmentId?: number;
  latitude: number;
  longitude: number;
  beforeId?: string;
  showDot?: boolean;
}

// All durations in milliseconds
const DURATION = {
  initial: {
    in: 300,
    out: 300
  },
  cycle: {
    in: 500,
    out: 500,
    pause: 500
  }
};

const PulsingMarker = ({ activeSegmentId, latitude, longitude, beforeId, showDot = true }: PulsingMarkerProps) => {
  const prevActiveSegmentIdRef = useRef(activeSegmentId);
  const [pulse, setPulse] = useState(0);
  const [, animate] = useAnimate();

  useEffect(() => {
    let controls;
    let timer;
    let running = true;

    const cycle = async () => {
      if (!running) return;
      controls = animate(0, 1, { duration: DURATION.cycle.in / 1000, ease: 'easeIn', onUpdate: value => setPulse(value) });
      await controls;

      if (!running) return;
      controls = animate(1, 2, { duration: DURATION.cycle.out / 1000, ease: 'easeOut', onUpdate: value => setPulse(value) });
      await controls;

      if (!running) return;
      await new Promise(resolve => {
        timer = setTimeout(resolve, DURATION.cycle.pause);
      });
      setPulse(0);

      cycle();
    };

    const runAnimation = async () => {
      // We only want to animate the initial pulse when adding new points. If the active segment ID hasn't changed we know we are adding new points instead of switching segments.
      if (activeSegmentId === prevActiveSegmentIdRef.current) {
        if (!running) return;
        controls = animate(0, 1, { duration: DURATION.cycle.in / 1000, ease: 'easeIn', onUpdate: value => setPulse(value) });
        await controls;

        if (!running) return;
        controls = animate(1, 2, { duration: DURATION.cycle.out / 1000, ease: 'easeOut', onUpdate: value => setPulse(value) });
        await controls;
      }

      cycle();
    };

    runAnimation();

    prevActiveSegmentIdRef.current = activeSegmentId;

    return () => {
      // Cancel recursive animation
      running = false;

      if (controls) {
        controls.stop();
      }

      if (timer) {
        clearTimeout(timer);
      }

      setPulse(0);
    };
  }, [animate, activeSegmentId, latitude, longitude]);

  const feature = useMemo(() => point([longitude, latitude]), [latitude, longitude]);

  return (
    <Source id={MARKER_PULSE_SOURCE_ID} type="geojson" data={feature}>
      <Layer
        id={MARKER_PULSE_LAYER_ID}
        source={MARKER_PULSE_SOURCE_ID}
        type="circle"
        paint={{
          'circle-radius': (84 / 2 / 2) * pulse, // Diameter from 0 to 84px
          'circle-color': 'transparent',
          'circle-stroke-color': COLOR_NEUTRAL_WHITE,
          'circle-stroke-width': 4 * (2 - pulse), // From 8px to 0
          'circle-stroke-opacity': pulse < 1 ? pulse : 2 - pulse // Increase from 0 to 1, then decrease back to 0
        }}
        beforeId={beforeId}
      />
      {showDot && (
        <Layer
          id="pulsing-marker-dot"
          source={MARKER_PULSE_SOURCE_ID}
          type="circle"
          paint={{
            'circle-radius': 3,
            'circle-color': COLOR_NEUTRAL_WHITE,
            'circle-stroke-width': 0
          }}
          beforeId={beforeId}
        />
      )}
    </Source>
  );
};

export default PulsingMarker;
